import { ReactElement } from 'react';
import { Box, Typography, Link } from '@mui/material';
import {
  Icon24Science,
  Icon24Art,
  Icon24Sports,
  Icon24Music,
  Icon24Celebrate,
  Icon24Books,
  Icon24Mountains,
  Icon24ChefHat,
} from '@care/react-embrace-icons';
import useFeatureFlag from '@/components/hooks/useFeatureFlag';
import { UI_FEATURE_FLAGS } from '@/constants';
import { AmplitudeEvent, trackAmplitudeEvent } from '@/lib/analyticsHelper';
import { useResponsive } from '@/components/hooks/useResponsive';

interface CategoriesDisplayInfo {
  displayName: string;
  url: string;
  categoryIcon: ReactElement | undefined;
}

const categoryInfo: CategoriesDisplayInfo[] = [
  {
    displayName: 'STEM',
    url: '/explore/d/stem',
    categoryIcon: <Icon24Science size={48} />,
  },
  {
    displayName: 'Art',
    url: '/explore/d/art',
    categoryIcon: <Icon24Art size={48} />,
  },
  {
    displayName: 'Sports',
    url: '/explore/d/sports',
    categoryIcon: <Icon24Sports size={48} />,
  },
  {
    displayName: 'Music',
    url: '/explore/d/music',
    categoryIcon: <Icon24Music size={48} />,
  },
  {
    displayName: 'Dance',
    url: '/explore/d/dance',
    categoryIcon: <Icon24Celebrate size={48} />,
  },
  {
    displayName: 'Academics',
    url: '/explore/d/academic',
    categoryIcon: <Icon24Books size={48} />,
  },
  {
    displayName: 'Adventure',
    url: '/explore/d/outdoor-and-adventure',
    categoryIcon: <Icon24Mountains size={48} />,
  },
  {
    displayName: 'Cooking',
    url: '/explore/d/cooking',
    categoryIcon: <Icon24ChefHat size={48} />,
  },
];

export const CategorySection = () => {
  const { isSmallViewport } = useResponsive();
  const onCategoryClick = (category: string) => {
    trackAmplitudeEvent(AmplitudeEvent.seoFeatureInteracted, {
      category,
      feature_name: 'Category tile',
    });
  };

  const isCookingCategoryFlagEnabled =
    useFeatureFlag(UI_FEATURE_FLAGS.OOH_ACTIVITIES_COOKING_CATEGORY.id)?.variationIndex === 0;

  const visibleCategories = categoryInfo.filter(
    (category) => isCookingCategoryFlagEnabled || category.displayName !== 'Cooking'
  );

  return (
    <Box mb={{ xs: 6, sm: 8 }}>
      <Typography
        variant={isSmallViewport ? 'h3' : 'h1'}
        sx={{
          textAlign: {
            xs: 'left',
            sm: 'center',
          },
          mx: { xs: 3, sm: 'auto' },
          mb: { xs: 3, sm: 6 },
        }}>
        Popular categories
      </Typography>
      <Box
        sx={{
          display: 'flex',
          justifyContent: { xs: 'flex-start', sm: 'center' },
          flexDirection: { xs: 'row', sm: 'colmn' },
          flexWrap: { xs: 'no-wrap', sm: 'wrap' },
          overflowX: { xs: 'scroll', sm: 'auto' },
          '&::-webkit-scrollbar': {
            display: 'none',
          },
          '-ms-overflow-style': 'none',
          'scrollbar-width': 'none',
          gap: { md: 4 },
        }}>
        {visibleCategories.map(({ displayName, url, categoryIcon }) => (
          <Box minWidth="96px" key={displayName}>
            <Link
              key={displayName}
              href={url}
              variant="link2"
              onClick={() => onCategoryClick(displayName)}
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                flexDirection: 'column',
                color: 'text.primary',
              }}>
              <Box>{categoryIcon}</Box>

              <Typography variant="inherit">{displayName}</Typography>
            </Link>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default CategorySection;
